import {
  IEmailLeaked,
  IImageLeak,
  IImageLeakList,
  IRawLeak,
  LeakData,
  LeakSeverity,
  LeakStatus,
  LeakType,
} from 'models/leak.model'
import api from './api'
import { IImageLeakCompanySummary, IReport } from 'models/report.model'

const getUserLeaksFiltred = async (filter: string): Promise<Array<IEmailLeaked>> => {
  const { data } = await api.get<Array<IEmailLeaked>>(`/v1/leaks?filter=${filter}`)
  return data
}

const getUserLeaks = async (): Promise<IEmailLeaked> => {
  const { data } = await api.get<IEmailLeaked>('/v1/leaks')
  return data
}

const updateUserLeak = async (email: string, site: string, date: string) => {
  await api.put('/v1/leaks', {
    email,
    site,
    date,
  })
}

const getLeaksFiltered = async (filter, type) => {
  const { data } = await api.get(`/v1/leaks/${type}?filter=${filter}`)
  return data
}

export type WebLeakWithReport<
  T extends LeakType.PHONE | LeakType.NAME | LeakType.ADDRESS | LeakType.IMAGE,
> = {
  id: string
  active: boolean
  status: LeakStatus
  value: string
  severity: LeakSeverity
  leakType: T
  data: (LeakData & { leakType: T })['data']
  report:
    | (Omit<IReport, '_id' | 'leakId' | 'registerId' | 'issues' | 'updatedBy' | 'version'> & {
        id: string
      })
    | null
}

export type GetLeaksWithReportsFilteredInputDto<
  T extends LeakType.PHONE | LeakType.NAME | LeakType.ADDRESS | LeakType.IMAGE,
> = {
  filter: string
  type: T
  cognitoId: string
}

const getLeaksWithReportsFiltered = async <
  T extends LeakType.PHONE | LeakType.NAME | LeakType.ADDRESS | LeakType.IMAGE,
>({
  filter,
  type,
  cognitoId,
}: GetLeaksWithReportsFilteredInputDto<T>): Promise<WebLeakWithReport<T>[]> => {
  const { data } = await api.get(`/v2/user/${cognitoId}/leak/${type}?filter=${filter}`)
  return data
}

export const getAllUserLeaks = async (cognitoId: string): Promise<IRawLeak[]> => {
  const { data } = await api.get<IRawLeak[]>(`/v2/user/${cognitoId}/leak`)
  return data
}

export const createExposureReport = async (
  cognitoId: string,
  leakId: string,
  issues?: string[],
) => {
  const { data } = await api.post(`/v2/user/${cognitoId}/leak/${leakId}/report`, { issues })
  return data
}

const ignoreExposureLeak = async (cognitoId: string, leakId: string) => {
  const { data } = await api.post(`/v2/user/${cognitoId}/leak/${leakId}/ignore`)
  return data
}

const markExposureAsSafe = async (cognitoId: string, leakId: string) => {
  await api.patch(`/v2/user/${cognitoId}/leak/${leakId}/safe`)
}

export const markFindingsAddressed = async (
  cognitoId: string,
  leakId: string,
  recommendationPriorities: number[],
) => {
  await api.patch(`/v2/user/${cognitoId}/risk-assessment/recommendations/addressed-findings`, {
    leakId,
    recommendationPriorities,
  })
}

export const updateExposureLeak = async (
  value: string | undefined | null,
  site: string,
  date: string,
  type: string,
) => {
  await api.put(`/v1/leaks/${type}`, {
    value,
    site,
    date,
  })
}

const createReport = async (type: string, siteUrl: string, leakDate: string) => {
  const { data } = await api.post(`/v1/reports/exposure`, {
    type,
    siteUrl,
    leakDate,
  })
  return data
}

const cancelReport = async (cognitoId: string, reportId: string) => {
  await api.patch(`/v2/user/${cognitoId}/report/${reportId}/cancel`)
}

const deleteReport = async (cognitoId: string, reportId: string) => {
  await api.delete(`/v2/user/${cognitoId}/report/${reportId}`)
}

const deleteImage = async (cognitoId: string, leakId: string) => {
  await api.delete(`/v2/user/${cognitoId}/leak/${leakId}`)
}

const removeWrongImages = images =>
  images.filter(
    image =>
      image.site &&
      image.site !== '' &&
      image.src &&
      image.src != '' &&
      image.title &&
      image.title != '',
  )

const filterImagesByFilter = (imageLeaksResponse, filterSelect) => {
  if (imageLeaksResponse) {
    return imageLeaksResponse.filter(image => image.state === filterSelect)
  }
}

const getImageLeakList = async (status = 'PENDING'): Promise<IImageLeakList> => {
  const byStatus = status ? `?status=${status}` : ''
  const { data } = await api.get<IImageLeakList>(`/v1/user/image-reverse-search${byStatus}`)
  const rightImages = removeWrongImages(data.images)
  const filteredImages = filterImagesByFilter(rightImages, status)
  const newData = { ...data, images: filteredImages }
  return newData
}

const convertToImageLeak = (images): IImageLeakList => {
  const imageLeaks = images.map((image: IReport) => {
    return {
      state: image.state,
      src: image.s3Url,
      domain: '',
      site: image.siteUrl,
      title: '',
      url: image.sourceUrl,
      id: image._id,
      details: image.details,
      reasons: image.denyReasons,
    }
  })

  return { images: imageLeaks }
}

const getReportedImageLeakList = async (): Promise<IImageLeakList> => {
  const { data } = await api.get<Array<IReport>>(`/v1/reports/user`)
  const reportedImages = convertToImageLeak(data)
  return reportedImages
}

const getCompanyListSummary = async (): Promise<IImageLeakCompanySummary> => {
  const { data } = await api.get<IImageLeakCompanySummary>(`/v1/reports/company`)
  return data
}

const patchImageLeak = async (imageLeak: Partial<IImageLeak>): Promise<IImageLeakList> => {
  const partialImageLeak = {
    src: imageLeak.src,
    url: imageLeak.url,
    state: imageLeak.state,
  }

  const { data: res } = await api.patch(`/v1/user/image-reverse-search`, {
    images: [partialImageLeak],
  })
  return res
}

const createReportImage = async (
  issues: Array<string>,
  type: string,
  s3Url: string,
  sourceUrl: string,
  siteUrl: string,
): Promise<IImageLeakList> => {
  const { data } = await api.post(`/v1/reports`, {
    issues,
    type,
    s3Url,
    sourceUrl,
    siteUrl,
  })
  return data
}

export default {
  getUserLeaksFiltred,
  getUserLeaks,
  getLeaksFiltered,
  getImageLeakList,
  getReportedImageLeakList,
  getCompanyListSummary,
  getLeaksWithReportsFiltered,
  getAllUserLeaks,
  updateUserLeak,
  updateExposureLeak,
  patchImageLeak,
  createReportImage,
  createReport,
  cancelReport,
  deleteReport,
  createExposureReport,
  ignoreExposureLeak,
  markExposureAsSafe,
  markFindingsAddressed,
  deleteImage,
}
